import { getToken, deleteToken } from 'firebase/messaging';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, auth, getMessagingIfSupported } from './firebase';

const VAPID_KEY =
  'BCNE3i-5z9nWzT8S2SOfyvNc2-SXrXKkiydjZfCq-l6qrQy23txJrQcIByqOTC311xXCSNroJPBnLenN34GMoLs';

// The push worker gets its OWN scope so it never collides with the app's
// main service worker (which lives at "/"). Two workers can't share a scope:
// they would keep replacing each other and trigger the "new version
// available" refresh prompt in a loop.
const PUSH_SW_URL = '/firebase-messaging-sw.js';
const PUSH_SW_SCOPE = '/firebase-cloud-messaging-push-scope';

// Functions are deployed to us-central1 (matches the rest of the app).
const functions = getFunctions(app, 'us-central1');
const registerPushToken = httpsCallable(functions, 'registerPushToken');
const unregisterPushToken = httpsCallable(functions, 'unregisterPushToken');

// Last registration error (kept so debugging can be switched back on).
let lastPushError = '';
export function getLastPushError() {
  return lastPushError;
}

// ── "Turned off" preference ──
// Remembers, per account on this device, that the user switched push off in
// Settings. Without it, the quiet re-registration on login would switch
// push straight back on, because the browser permission stays "granted".
function offKey() {
  const uid = auth.currentUser?.uid;
  return uid ? `veylPushOff_${uid}` : null;
}

export function isPushTurnedOff() {
  try {
    const key = offKey();
    return key ? localStorage.getItem(key) === '1' : false;
  } catch {
    return false;
  }
}

export function setPushTurnedOff(off) {
  try {
    const key = offKey();
    if (!key) return;
    if (off) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch { /* storage unavailable */ }
}

/**
 * Can this browser do push at all?
 * (False in some in-app browsers, older Safari, and iPhones where Veyl
 * hasn't been added to the home screen.)
 */
export async function isPushSupported() {
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return false;
  return (await getMessagingIfSupported()) !== null;
}

/**
 * 'granted' | 'denied' | 'default' | 'unsupported'
 * 'default' means the user hasn't been asked yet.
 */
export async function getPushStatus() {
  if (!(await isPushSupported())) return 'unsupported';
  return Notification.permission;
}

async function getCurrentToken() {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return null;

  const registration = await navigator.serviceWorker.register(PUSH_SW_URL, {
    scope: PUSH_SW_SCOPE,
  });
  await navigator.serviceWorker.ready;

  return getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
}

/**
 * Ask permission (only if not asked yet), then register this device.
 * Call this from a button click, never on page load: if the user taps
 * "Block" on a surprise prompt, the browser remembers it permanently.
 *
 * Returns 'granted' | 'denied' | 'default' | 'unsupported' | 'error'
 */
export async function enablePush() {
  let stage = 'start';
  lastPushError = '';
  try {
    stage = 'support check';
    if (!(await isPushSupported())) {
      lastPushError = 'support check: this browser reports push is not supported';
      return 'unsupported';
    }

    stage = 'permission prompt';
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      lastPushError = `permission prompt: result was "${permission}"`;
      return permission;
    }

    stage = 'getting device token';
    const token = await getCurrentToken();
    if (!token) {
      lastPushError = 'getting device token: an empty token came back';
      return 'error';
    }

    stage = 'saving device to Veyl';
    await registerPushToken({ token });
    setPushTurnedOff(false);
    return 'granted';
  } catch (err) {
    lastPushError = `${stage}: ${err?.code || ''} ${err?.message || err}`.trim();
    console.error('enablePush failed at', stage, err);
    return 'error';
  }
}

/**
 * Quietly re-register this device if permission was already granted.
 * Safe to call on every login: it refreshes the token and, importantly,
 * re-links it to whoever is signed in NOW (tokens belong to one account).
 * It never shows a prompt, and does nothing if the user turned push off.
 */
export async function refreshPushRegistration() {
  try {
    if (isPushTurnedOff()) return;
    if (!(await isPushSupported())) return;
    if (Notification.permission !== 'granted') return;
    const token = await getCurrentToken();
    if (token) await registerPushToken({ token });
  } catch (err) {
    console.error('refreshPushRegistration failed:', err);
  }
}

/**
 * Remove this device's token from the server and the browser.
 * Used on sign-out and by the Settings "turn off" button. Must be called
 * BEFORE the user is signed out, because the server only lets a token's
 * owner delete it. (This does NOT set the "turned off" preference, so
 * signing out and back in still re-enables push; the Settings button
 * sets that preference separately.)
 */
export async function disablePush() {
  try {
    const messaging = await getMessagingIfSupported();
    if (!messaging || Notification.permission !== 'granted') return;
    const token = await getCurrentToken();
    if (token) {
      await unregisterPushToken({ token });
      await deleteToken(messaging);
    }
  } catch (err) {
    console.error('disablePush failed:', err);
  }
}